/** Pilot-mode helpers. `label` is BE-owned — never compute it. */

export function isNotInPilotError(error) {
  if (!error) return false;
  if (Number(error.status) === 423) return true;
  const body = error.body;
  return Boolean(body && (body.error === 'not_in_pilot' || body.locked === true));
}

export function isPilotCapReachedError(error) {
  if (!error) return false;
  const body = error.body;
  if (body?.error === 'pilot_cap_reached') return true;
  return Number(error.status) === 403 && /pilot_cap_reached|pilot covers/i.test(String(error.message || ''));
}

export function pilotErrorMessage(error, fallback = 'Please try again') {
  const body = error?.body;
  if (body?.error === 'pilot_cap_reached' && body.message) return body.message;
  return body?.message || error?.message || fallback;
}

export function isPilotModeOn(status) {
  return Boolean(status?.pilot_mode);
}

/** over_cap means treat as at cap even when used > cap. Never show "15 of 10". */
export function isPilotAtCap(status) {
  if (!isPilotModeOn(status)) return false;
  if (status.over_cap === true) return true;
  const remaining = Number(status.remaining);
  if (Number.isFinite(remaining) && remaining <= 0) return true;
  const cap = Number(status.cap);
  const used = Number(status.used);
  return Number.isFinite(cap) && Number.isFinite(used) && used >= cap;
}

export function pilotLockChipLabel(status) {
  const cap = Number(status?.cap);
  if (!Number.isFinite(cap) || cap <= 0) return 'Pilot full';
  return `${cap} of ${cap} used`;
}

export const PILOT_NO_SWAP_TOOLTIP = 'You’ve reached the maximum number of accounts allowed in this pilot.';

/**
 * Under cap: remaining-first copy from `remaining` (never cap − used).
 * At / over cap: BE `label` verbatim so over-cap never reads "15 of 10".
 */
export function formatPilotAllowanceCopy(status) {
  if (!isPilotModeOn(status)) return null;
  if (isPilotAtCap(status)) {
    return status.label || null;
  }
  const remaining = Number(status.remaining);
  const cap = Number(status.cap);
  if (!Number.isFinite(remaining) || !Number.isFinite(cap) || cap <= 0) {
    return status.label || null;
  }
  return `${remaining} of ${cap} picks left`;
}

/** Fill used/cap, but never more than cap — over_cap still reads as a full bar. */
export function pilotProgress(status) {
  const cap = Number(status?.cap);
  if (!Number.isFinite(cap) || cap <= 0) return { filled: 0, cap: 0 };
  if (isPilotAtCap(status)) return { filled: cap, cap };
  const used = Number(status.used);
  const filled = Number.isFinite(used) ? Math.min(cap, Math.max(0, used)) : 0;
  return { filled, cap };
}

/**
 * At-cap teaser under the BE label.
 * SAM 54 / cap 10 → "44 more accounts qualify"; priority 30 / cap 10 → "20 in your priority tier."
 */
export function formatPilotCapTeaser(summary, status, { inThisView = false } = {}) {
  const cap = Number(status?.cap);
  if (!Number.isFinite(cap) || cap <= 0) return null;
  const sam = pickFunnelCount(summary, ['in_market', 'sam']);
  const priority = pickFunnelCount(summary, ['priority', 'priority_now']);
  const more = sam != null ? Math.max(0, sam - cap) : null;
  const priorityLeft = priority != null ? Math.max(0, priority - cap) : null;
  if (more == null) return null;
  const view = inThisView ? ' in this view' : '';
  if (priorityLeft == null) {
    return `${more} more accounts qualify${view}.`;
  }
  return `${more} more accounts qualify${view}, ${priorityLeft} in your priority tier.`;
}

function pickFunnelCount(summary, keys) {
  const stages = Array.isArray(summary?.funnel_stages) ? summary.funnel_stages : [];
  const byStage = Object.fromEntries(stages.filter((s) => s?.key).map((s) => [s.key, s]));
  const f = summary?.funnel && !Array.isArray(summary.funnel) ? summary.funnel : {};
  for (const k of keys) {
    const fromObj = asCount(f[k]);
    if (fromObj != null) return fromObj;
    const fromStage = asCount(byStage[k]?.count);
    if (fromStage != null) return fromStage;
  }
  return null;
}

function asCount(value) {
  if (value == null) return null;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'object') {
    const n = Number(value.count ?? value.total ?? value.n);
    return Number.isFinite(n) ? n : null;
  }
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

export function navigateToPilotLockedPage(navigate, createPageUrl, body = {}) {
  const id = body.account_id || body.account_name || 'locked';
  navigate(createPageUrl(`AccountFinal?id=${encodeURIComponent(id)}`), {
    state: { pilotLocked: body },
  });
}
